import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
/* eslint-enable no-restricted-imports */
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";

import type { UserEventRole } from "@spwn-portal/grpc";

// APIが返すロールのうち、Portalから遷移先を提供できるダッシュボード権限だけを扱う。
const DASHBOARD_ROLES = [
  "ticket_dashboard_viewer",
  "sales_dashboard_viewer",
] as const;

export type DashboardRole = (typeof DASHBOARD_ROLES)[number];

export type DashboardEventRole = {
  eventId: string;
  roles: DashboardRole[];
};

const isDashboardRole = (role: string): role is DashboardRole =>
  DASHBOARD_ROLES.some((dashboardRole) => dashboardRole === role);

/**
 * Backend側で別用途のロールが追加されても一覧画面へ空の導線を出さないよう、
 * Portalが対応しているダッシュボード権限だけをイベント単位へ変換する。
 */
const toDashboardEventRoles = (
  eventRoles: readonly UserEventRole[]
): DashboardEventRole[] =>
  eventRoles.flatMap(({ eventFirestoreId, role }) => {
    const roles = role.filter(isDashboardRole);

    return roles.length > 0
      ? [
          {
            eventId: eventFirestoreId,
            roles,
          },
        ]
      : [];
  });

const GetDashboardEventRolesKey = "getDashboardEventRoles";

/**
 * ログインユーザーが閲覧できるダッシュボード権限を取得する。
 * 未ログイン時はリクエストせず、API errorは後続画面で認可判定できるよう加工せず返す。
 */
export const useGetDashboardEventRoles = () => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const connectClient = useConnectClient(LegacyPortalDashboardService);

  const {
    data: eventRoles = [],
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<DashboardEventRole[], Error>(
    // ユーザー切り替え前の権限を流用しないようUID単位でキャッシュし、未ログイン時は取得を停止する。
    currentUserId ? [GetDashboardEventRolesKey, currentUserId] : null,
    async () => {
      const response = await connectClient.getEventRoles({});

      return toDashboardEventRoles(response.eventRoles);
    }
  );

  return {
    eventRoles,
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
